/**
 * NotificationFilters — Surface Layer (/01).
 *
 * Presentational filter control for the notifications panel, modeled on the
 * IBM Products `NotificationsPanel` "all / unread" toggle and composed locally
 * from a `@carbon/react` ContentSwitcher + Switch. Fully controlled: it owns no
 * state and reports changes via `onChange`. The Switch `name` carries the filter
 * value, so selection maps back to a typed union without index math.
 */

import { ContentSwitcher, Switch } from '@carbon/react';
import './notifications-panel.scss';

export type NotificationFilterValue = 'all' | 'unread';

export interface NotificationFiltersProps {
  /** Currently selected filter. */
  value: NotificationFilterValue;
  /** Called with the next filter when the user switches. */
  onChange: (value: NotificationFilterValue) => void;
  /** Count of unread notifications, shown on the "Unread" switch. */
  unreadCount: number;
}

const ORDER: NotificationFilterValue[] = ['all', 'unread'];

export function NotificationFilters({ value, onChange, unreadCount }: NotificationFiltersProps) {
  const selectedIndex = Math.max(0, ORDER.indexOf(value));

  return (
    <div className="app-notifications-panel__filters">
      <ContentSwitcher
        size="sm"
        selectedIndex={selectedIndex}
        onChange={({ name }) => {
          if (name === 'all' || name === 'unread') {
            onChange(name);
          }
        }}
      >
        <Switch name="all" text="All" />
        <Switch name="unread" text={`Unread (${unreadCount})`} />
      </ContentSwitcher>
    </div>
  );
}
